import { useState, useEffect, useCallback, useRef } from "react";

const SB_URL = "https://lqnfkimcoqzlbegyuxnj.supabase.co";
const SB_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxxbmZraW1jb3F6bGJlZ3l1eG5qIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODUxNDI3MDEsImV4cCI6MjEwMDcxODcwMX0.aPeKRH_D6v8JDpzCaaji8GIeV2s06qxfIwdj-OJDndE";

const sb = async (path, method = "GET", body = null) => {
  const res = await fetch(`${SB_URL}/rest/v1/${path}`, {
    method,
    headers: { apikey: SB_KEY, Authorization: `Bearer ${SB_KEY}`, "Content-Type": "application/json", Prefer: "return=representation" },
    body: body ? JSON.stringify(body) : null,
  });
  const text = await res.text();
  if (!res.ok) throw new Error(text);
  return text ? JSON.parse(text) : [];
};

const C = { accent:"#6C63FF", success:"#22C55E", warning:"#F59E0B", danger:"#EF4444", purple:"#A78BFA", bg:"#0D0F1A", surface:"#161929", card:"#1C2035", border:"#252A40", muted:"#6B7280", text:"#F0F0F8" };
const CL = { accent:"#6C63FF", success:"#22C55E", warning:"#F59E0B", danger:"#EF4444", purple:"#A78BFA", bg:"#F4F5FB", surface:"#FFFFFF", card:"#ECEEF8", border:"#D1D5DB", muted:"#6B7280", text:"#111827" };

const STATUS_FLOW = ["Not Started","Started","In Progress","Sent","Completed"];
const STATUS_COLOR = { "Not Started":"#6B7280","Started":"#38BDF8","In Progress":"#F59E0B","Sent":"#A78BFA","Completed":"#22C55E" };
const CATEGORIES = ["Reel","Logo","Poster","Website","Visiting Card","Video Edit","Photo Edit","Content","Other"];
const TAGS = ["Urgent","VIP","Monthly","One Time","Repeat Client","High Priority","Design","Marketing"];
const PRIORITIES = ["Low","Medium","High"];
const PRIORITY_COLOR = { Low:"#22C55E", Medium:"#F59E0B", High:"#EF4444" };
const PRIORITY_STARS = { Low:1, Medium:2, High:3 };

const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-IN",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}) : "";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "";
const todayStr = () => new Date().toDateString();
const isOverdue = (dl,st) => dl && st!=="Completed" && new Date(dl)<new Date();

function useBreakpoint() {
  const [w,setW] = useState(window.innerWidth);
  useEffect(()=>{ const fn=()=>setW(window.innerWidth); window.addEventListener("resize",fn); return ()=>window.removeEventListener("resize",fn); },[]);
  return { isMobile:w<768, isTablet:w>=768&&w<1024, isDesktop:w>=1024, w };
}

export default function App() {
  const [dark,setDark] = useState(true);
  const T = dark ? C : CL;
  const bp = useBreakpoint();
  const [clients,setClients] = useState([]);
  const [works,setWorks] = useState([]);
  const [loading,setLoading] = useState(true);
  const [view,setView] = useState("dashboard");
  const [activeClient,setActiveClient] = useState(null);
  const [activeWork,setActiveWork] = useState(null);
  const [searchQ,setSearchQ] = useState("");
  const [toast,setToast] = useState(null);
  const [modal,setModal] = useState(null);

  const notify = (msg,type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),2800); };

  const loadAll = useCallback(async()=>{
    try {
      setLoading(true);
      const [cls,wks,revs,progs,tls] = await Promise.all([
        sb("clients?order=created_at.desc"),
        sb("works?order=created_at.desc"),
        sb("revisions?order=created_at.asc"),
        sb("progress?order=created_at.asc"),
        sb("timeline?order=created_at.asc"),
      ]);
      setClients(cls);
      setWorks(wks.map(w=>({...w,tags:w.tags||[],revisions:revs.filter(r=>r.work_id===w.id),progress:progs.filter(p=>p.work_id===w.id),timeline:tls.filter(t=>t.work_id===w.id)})));
    } catch(e){ notify("Load error","error"); } finally { setLoading(false); }
  },[]);

  useEffect(()=>{ loadAll(); },[loadAll]);

  const syncWork = u => { setWorks(p=>p.map(w=>w.id===u.id?u:w)); if(activeWork?.id===u.id) setActiveWork(u); };

  const addClient = async d => { const [c]=await sb("clients","POST",d); setClients(p=>[c,...p]); notify("Client added!"); };
  const addWork = async d => {
    const [w]=await sb("works","POST",{...d,budget:+d.budget||0,advance:+d.advance||0});
    await sb("timeline","POST",{work_id:w.id,event:"Project Created"});
    setWorks(p=>[{...w,tags:w.tags||[],revisions:[],progress:[],timeline:[{event:"Project Created",created_at:new Date().toISOString()}]},...p]);
    notify("Work created!");
  };
  const advanceStatus = async w => {
    const idx=STATUS_FLOW.indexOf(w.status); if(idx>=STATUS_FLOW.length-1) return;
    const next=STATUS_FLOW[idx+1];
    await sb(`works?id=eq.${w.id}`,"PATCH",{status:next});
    await sb("timeline","POST",{work_id:w.id,event:`Moved to ${next}`});
    syncWork({...w,status:next,timeline:[...w.timeline,{event:`Moved to ${next}`,created_at:new Date().toISOString()}]});
    notify(`→ ${next}`);
  };
  const addRevision = async (w,note) => {
    const [rev]=await sb("revisions","POST",{work_id:w.id,note});
    const num=w.revisions.length+1;
    await sb("timeline","POST",{work_id:w.id,event:`Revision ${num} Added`});
    syncWork({...w,revisions:[...w.revisions,rev],timeline:[...w.timeline,{event:`Revision ${num} Added`,created_at:new Date().toISOString()}]});
    notify(`Revision ${num} added!`);
  };
  const addProgress = async (w,line) => { const [p]=await sb("progress","POST",{work_id:w.id,line}); syncWork({...w,progress:[...w.progress,p]}); notify("Progress saved!"); };
  const toggleDelivered = async w => {
    const val=!w.delivered;
    await sb(`works?id=eq.${w.id}`,"PATCH",{delivered:val});
    let tl=w.timeline;
    if(val){await sb("timeline","POST",{work_id:w.id,event:"Delivered"}); tl=[...tl,{event:"Delivered",created_at:new Date().toISOString()}];}
    syncWork({...w,delivered:val,timeline:tl}); notify(val?"Delivered!":"Undone");
  };
  const setPayment = async (w,state) => {
    const advance_received=state==="advance"||state==="full", paid=state==="full";
    await sb(`works?id=eq.${w.id}`,"PATCH",{advance_received,paid});
    let tl=w.timeline;
    if(state==="advance"&&!w.advance_received){await sb("timeline","POST",{work_id:w.id,event:"Advance Payment Received"}); tl=[...tl,{event:"Advance Payment Received",created_at:new Date().toISOString()}];}
    if(state==="full"&&!w.paid){await sb("timeline","POST",{work_id:w.id,event:"Full Payment Received"}); tl=[...tl,{event:"Full Payment Received",created_at:new Date().toISOString()}];}
    syncWork({...w,advance_received,paid,timeline:tl}); notify(state==="full"?"💰 Paid!":state==="advance"?"Advance received!":"Reset");
  };
  const saveNotes = async (w,notes) => { await sb(`works?id=eq.${w.id}`,"PATCH",{notes}); syncWork({...w,notes}); };

  const todayWorks=works.filter(w=>new Date(w.created_at).toDateString()===todayStr());
  const pendingWorks=works.filter(w=>w.status!=="Completed");
  const completedWorks=works.filter(w=>w.status==="Completed");
  const overdueWorks=works.filter(w=>isOverdue(w.deadline,w.status));
  const todayRevisions=works.filter(w=>w.revisions.some(r=>new Date(r.created_at).toDateString()===todayStr()));
  const unpaidWorks=works.filter(w=>!w.paid);
  const clientWorks=cid=>works.filter(w=>w.client_id===cid);
  const totalBudget=works.reduce((a,w)=>a+(w.budget||0),0);
  const totalReceived=works.reduce((a,w)=>a+(w.paid?(w.budget||0):w.advance_received?(w.advance||0):0),0);

  const openClient=c=>{setActiveClient(c);setView("client-detail");};
  const openWork=w=>{setActiveWork(w);setView("work-detail");};
  const goBack=()=>{ if(view==="work-detail"){setView("client-detail");setActiveWork(null);} else{setView("clients");setActiveClient(null);} };
  const navTo=v=>{setView(v);setSearchQ("");};

  const searchResults=searchQ.length>1?{
    clients:clients.filter(c=>`${c.name} ${c.company||""}`.toLowerCase().includes(searchQ.toLowerCase())),
    works:works.filter(w=>`${w.title} ${w.category} ${w.status}`.toLowerCase().includes(searchQ.toLowerCase())),
  }:null;

  if(loading) return <Loader T={T}/>;

  const isClientOrWork = view==="client-detail"||view==="work-detail";

  return (
    <div style={{minHeight:"100vh",background:T.bg,fontFamily:"'Inter','Segoe UI',system-ui,sans-serif",color:T.text,paddingBottom:bp.isMobile?70:0}}>
      <style>{`*{box-sizing:border-box;-webkit-tap-highlight-color:transparent;}input,textarea,select{-webkit-appearance:none;}button{cursor:pointer;font-family:inherit;}::-webkit-scrollbar{width:3px;}::-webkit-scrollbar-thumb{background:${T.border};border-radius:4px;}`}</style>
      {toast&&<Toast msg={toast.msg} type={toast.type}/>}

      {/* HEADER */}
      <header style={{background:T.surface,borderBottom:`1px solid ${T.border}`,padding:bp.isMobile?"12px 16px":"14px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,zIndex:100}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          {isClientOrWork&&<button onClick={goBack} style={{background:"none",border:`1px solid ${T.border}`,color:T.muted,borderRadius:8,padding:"5px 12px",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",gap:4}}>← Back</button>}
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{width:30,height:30,background:C.accent,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <span style={{fontWeight:800,fontSize:bp.isMobile?15:17,color:T.text,letterSpacing:"-0.3px"}}>Workflow</span>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          {!bp.isMobile&&(
            <div style={{position:"relative"}}>
              <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:T.muted,fontSize:13}}>🔍</span>
              <input value={searchQ} onChange={e=>{setSearchQ(e.target.value);if(e.target.value)setView("search");else setView("dashboard");}}
                style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,padding:"8px 12px 8px 32px",fontSize:13,outline:"none",width:220}}
                placeholder="Search clients, work…"/>
            </div>
          )}
          <button onClick={()=>setDark(d=>!d)} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 10px",fontSize:15,color:T.muted}}>{dark?"☀️":"🌙"}</button>
          <div style={{width:32,height:32,borderRadius:"50%",background:C.accent,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,color:"#fff"}}>W</div>
        </div>
      </header>

      {/* DESKTOP NAV */}
      {!bp.isMobile&&(
        <nav style={{background:T.surface,borderBottom:`1px solid ${T.border}`,padding:"0 28px",display:"flex",gap:4}}>
          {[["dashboard","📊 Dashboard"],["clients","👥 Clients"]].map(([k,l])=>(
            <button key={k} onClick={()=>navTo(k)}
              style={{background:"none",border:"none",color:(view===k||view==="client-detail"&&k==="clients"||view==="work-detail"&&k==="clients")?C.accent:T.muted,padding:"13px 18px",fontSize:14,fontWeight:700,borderBottom:`3px solid ${(view===k||view==="client-detail"&&k==="clients"||view==="work-detail"&&k==="clients")?C.accent:"transparent"}`,transition:"all 0.15s"}}>
              {l}
            </button>
          ))}
        </nav>
      )}

      {/* MAIN */}
      <main style={{padding:bp.isMobile?"14px 14px":"24px 28px",maxWidth:"100%"}}>

        {/* SEARCH */}
        {view==="search"&&(
          <div>
            <h2 style={{color:T.text,fontWeight:800,fontSize:20,marginBottom:16}}>"{searchQ}"</h2>
            {searchResults?.clients.map(c=>(
              <div key={c.id} onClick={()=>openClient(c)} style={{background:T.surface,borderRadius:12,padding:"14px 16px",marginBottom:10,cursor:"pointer",display:"flex",alignItems:"center",gap:12,border:`1px solid ${T.border}`}}>
                <Av name={c.name} size={38}/><div><b style={{color:T.text}}>{c.name}</b><div style={{color:T.muted,fontSize:12}}>{c.company}</div></div>
              </div>
            ))}
            {searchResults?.works.map(w=>(
              <div key={w.id} onClick={()=>openWork(w)} style={{background:T.surface,borderRadius:12,padding:"14px 16px",marginBottom:10,cursor:"pointer",display:"flex",alignItems:"center",gap:12,border:`1px solid ${T.border}`}}>
                <StatusDot status={w.status}/><div style={{flex:1}}><b style={{color:T.text}}>{w.title}</b><div style={{color:T.muted,fontSize:12}}>{w.category}</div></div><SBadge status={w.status}/>
              </div>
            ))}
          </div>
        )}

        {/* DASHBOARD */}
        {view==="dashboard"&&(
          <div>
            {/* Overdue Alert */}
            {overdueWorks.length>0&&(
              <div style={{background:"#EF444420",border:"1px solid #EF444455",borderRadius:12,padding:"12px 16px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{display:"flex",gap:10,alignItems:"center"}}>
                  <span style={{fontSize:18}}>🚨</span>
                  <div>
                    <div style={{color:C.danger,fontWeight:700,fontSize:13}}>Overdue Invoice Alert</div>
                    <div style={{color:T.muted,fontSize:12}}>{overdueWorks.map(w=>`${w.title} is overdue`).join(" · ")}</div>
                  </div>
                </div>
                <button style={{background:C.danger,color:"#fff",border:"none",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700}}>RESOLVE</button>
              </div>
            )}

            {/* Stats Grid */}
            <div style={{display:"grid",gridTemplateColumns:bp.isMobile?"repeat(2,1fr)":bp.isTablet?"repeat(3,1fr)":"repeat(6,1fr)",gap:10,marginBottom:14}}>
              {[
                {label:"TODAY'S WORK",value:todayWorks.length,sub:`${works.filter(w=>w.priority==="High").length} high priority`,color:C.accent,icon:"📋"},
                {label:"PENDING",value:pendingWorks.length,sub:"Awaiting client",color:C.warning,icon:"⏳"},
                {label:"COMPLETED",value:completedWorks.length,sub:"This week",color:C.success,icon:"✅"},
                {label:"REVISIONS",value:todayRevisions.length,sub:"Needs attention",color:C.purple,icon:"🔄"},
                {label:"PAYMENTS",value:unpaidWorks.length,sub:"Invoices drafted",color:C.info||"#38BDF8",icon:"💳"},
                {label:"OVERDUE",value:overdueWorks.length,sub:overdueWorks.length>0?"Critical":"All good",color:C.danger,icon:"🚨"},
              ].map(({label,value,sub,color,icon})=>(
                <div key={label} style={{background:T.surface,borderRadius:12,padding:bp.isMobile?"12px 10px":"14px 16px",border:`1px solid ${T.border}`,position:"relative",overflow:"hidden"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                    <div style={{color:T.muted,fontSize:10,fontWeight:700,letterSpacing:"0.08em"}}>{label}</div>
                    <span style={{fontSize:14}}>{icon}</span>
                  </div>
                  <div style={{color,fontSize:bp.isMobile?24:28,fontWeight:900,lineHeight:1}}>{value}</div>
                  <div style={{color:T.muted,fontSize:11,marginTop:4}}>{sub}</div>
                  <div style={{position:"absolute",bottom:0,left:0,right:0,height:2,background:color+"44"}}/>
                </div>
              ))}
            </div>

            {/* Revenue */}
            <div style={{background:T.surface,borderRadius:14,padding:bp.isMobile?"14px":"18px",border:`1px solid ${T.border}`,marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div>
                  <div style={{color:T.text,fontWeight:700,fontSize:14}}>Revenue</div>
                  <div style={{color:T.muted,fontSize:12}}>{new Date().toLocaleDateString("en-IN",{month:"long",year:"numeric"})}</div>
                </div>
                <div style={{color:C.accent,fontWeight:900,fontSize:18}}>₹{totalBudget.toLocaleString()}</div>
              </div>
              {/* Progress bar */}
              <div style={{background:T.card,borderRadius:4,height:8,marginBottom:14,overflow:"hidden"}}>
                <div style={{background:`linear-gradient(90deg,${C.accent},${C.success})`,height:"100%",width:`${totalBudget>0?Math.min(100,totalReceived/totalBudget*100):0}%`,borderRadius:4,transition:"width 0.5s"}}/>
              </div>
              <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}><div style={{width:8,height:8,borderRadius:"50%",background:C.accent}}/><span style={{color:T.muted,fontSize:12}}>Received</span><span style={{color:T.text,fontWeight:700,fontSize:13}}>₹{totalReceived.toLocaleString()}</span></div>
                <div style={{display:"flex",alignItems:"center",gap:6}}><div style={{width:8,height:8,borderRadius:"50%",background:C.warning}}/><span style={{color:T.muted,fontSize:12}}>Pending</span><span style={{color:T.text,fontWeight:700,fontSize:13}}>₹{(totalBudget-totalReceived).toLocaleString()}</span></div>
              </div>
            </div>

            {/* Today's Work */}
            {todayWorks.length>0&&(
              <div style={{background:T.surface,borderRadius:14,padding:bp.isMobile?"14px":"18px",border:`1px solid ${T.border}`,marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                  <div style={{color:T.text,fontWeight:700,fontSize:14}}>Today's Work</div>
                  <button onClick={()=>navTo("clients")} style={{background:"none",border:"none",color:C.accent,fontSize:12,fontWeight:700}}>VIEW ALL</button>
                </div>
                {todayWorks.slice(0,5).map(w=>{
                  const cl=clients.find(c=>c.id===w.client_id);
                  return (
                    <div key={w.id} onClick={()=>openWork(w)} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:`1px solid ${T.border}`,cursor:"pointer"}}>
                      <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${w.status==="Completed"?C.success:T.border}`,background:w.status==="Completed"?C.success:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        {w.status==="Completed"&&<span style={{color:"#fff",fontSize:10}}>✓</span>}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{color:T.text,fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.title}</div>
                        <div style={{color:T.muted,fontSize:11}}>Client: {cl?.name||"—"}</div>
                        <div style={{display:"flex",gap:4,marginTop:4,flexWrap:"wrap"}}>
                          {w.priority==="High"&&<span style={{background:C.danger+"22",color:C.danger,borderRadius:20,padding:"1px 8px",fontSize:10,fontWeight:700}}>High Priority</span>}
                          {w.category&&<span style={{background:T.card,color:T.muted,borderRadius:20,padding:"1px 8px",fontSize:10}}>{w.category}</span>}
                        </div>
                      </div>
                      <StatusDot status={w.status}/>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Recent Activity + Pending Payments */}
            <div style={{display:"grid",gridTemplateColumns:bp.isMobile?"1fr":"1fr 1fr",gap:14}}>
              <div style={{background:T.surface,borderRadius:14,padding:bp.isMobile?"14px":"18px",border:`1px solid ${T.border}`}}>
                <div style={{color:T.text,fontWeight:700,fontSize:14,marginBottom:14}}>Recent Activity</div>
                {works.length===0&&<Empty icon="⚡" msg="No activity yet"/>}
                {works.slice(0,5).flatMap(w=>w.timeline.slice(-1).map((t,i)=>{
                  const cl=clients.find(c=>c.id===w.client_id);
                  return (
                    <div key={w.id+i} style={{display:"flex",gap:10,padding:"9px 0",borderBottom:`1px solid ${T.border}`,alignItems:"flex-start"}}>
                      <Av name={cl?.name||"W"} size={28}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{color:T.text,fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.title} — {t.event}</div>
                        <div style={{color:T.muted,fontSize:11}}>{fmt(t.created_at)}</div>
                      </div>
                    </div>
                  );
                })).slice(0,6)}
              </div>
              <div style={{background:T.surface,borderRadius:14,padding:bp.isMobile?"14px":"18px",border:`1px solid ${T.border}`}}>
                <div style={{color:T.text,fontWeight:700,fontSize:14,marginBottom:14}}>Pending Payments</div>
                {unpaidWorks.length===0&&<Empty icon="🎉" msg="All cleared!"/>}
                {unpaidWorks.map(w=>{
                  const cl=clients.find(c=>c.id===w.client_id);
                  return (
                    <div key={w.id} onClick={()=>openWork(w)} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"10px 0",borderBottom:`1px solid ${T.border}`,cursor:"pointer",gap:8}}>
                      <div style={{minWidth:0}}>
                        <div style={{color:T.text,fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.title}</div>
                        <div style={{color:T.muted,fontSize:11}}>{cl?.name} · {w.deadline?`Due ${fmtDate(w.deadline)}`:""}</div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <div style={{color:C.danger,fontWeight:800,fontSize:13}}>₹{((w.budget||0)-(w.advance||0)).toLocaleString()}</div>
                        {isOverdue(w.deadline,w.status)&&<div style={{color:C.danger,fontSize:10,fontWeight:700}}>OVERDUE</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* CLIENTS */}
        {view==="clients"&&(
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div>
                <h2 style={{color:T.text,fontWeight:800,fontSize:bp.isMobile?18:22,margin:0}}>Clients</h2>
                <div style={{color:T.muted,fontSize:12,marginTop:2}}>Manage your active network</div>
              </div>
              <button onClick={()=>setModal("add-client")} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"9px 18px",fontWeight:700,fontSize:13}}>+ New Client</button>
            </div>
            {/* Search clients */}
            <div style={{position:"relative",marginBottom:14}}>
              <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:T.muted}}>🔍</span>
              <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} placeholder="Search clients…"
                style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,padding:"10px 14px 10px 36px",fontSize:13,outline:"none",width:"100%"}}/>
            </div>
            {clients.filter(c=>`${c.name} ${c.company||""}`.toLowerCase().includes(searchQ.toLowerCase())).map(c=>{
              const cw=clientWorks(c.id);
              const active=cw.filter(w=>w.status!=="Completed").length;
              const blocked=cw.filter(w=>isOverdue(w.deadline,w.status)).length;
              return (
                <div key={c.id} onClick={()=>openClient(c)} style={{background:T.surface,borderRadius:14,padding:bp.isMobile?"12px 14px":"14px 18px",marginBottom:10,cursor:"pointer",display:"flex",alignItems:"center",gap:14,border:`1px solid ${T.border}`}}>
                  <Av name={c.name} size={bp.isMobile?40:46}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{color:T.text,fontWeight:700,fontSize:bp.isMobile?14:15,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</div>
                    {c.company&&<div style={{color:T.muted,fontSize:12}}>{c.company}</div>}
                    {c.phone&&<div style={{color:T.muted,fontSize:12}}>📱 {c.phone}</div>}
                    {c.email&&<div style={{color:T.muted,fontSize:12}}>✉️ {c.email}</div>}
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    {active>0&&<div style={{background:C.accent+"22",color:C.accent,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700,marginBottom:4}}>{active} Active</div>}
                    {blocked>0&&<div style={{background:C.danger+"22",color:C.danger,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700}}>{blocked} Blocked</div>}
                    {active===0&&blocked===0&&<div style={{color:T.muted,fontSize:12}}>{cw.length} works</div>}
                  </div>
                </div>
              );
            })}
            {clients.length===0&&<Empty icon="👥" msg="No clients yet. Add your first!"/>}
          </div>
        )}

        {/* CLIENT DETAIL */}
        {view==="client-detail"&&activeClient&&(
          <div>
            <div style={{background:T.surface,borderRadius:14,padding:bp.isMobile?"14px":"18px",marginBottom:16,border:`1px solid ${T.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                <div style={{display:"flex",gap:14,alignItems:"center"}}>
                  <Av name={activeClient.name} size={bp.isMobile?44:52}/>
                  <div>
                    <div style={{color:T.text,fontWeight:800,fontSize:bp.isMobile?16:19}}>{activeClient.name}</div>
                    {activeClient.company&&<div style={{background:C.accent+"22",color:C.accent,borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:600,display:"inline-block",marginTop:4}}>{activeClient.company}</div>}
                  </div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setModal("report")} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"8px 14px",fontWeight:700,fontSize:12}}>📄 Generate Report</button>
                </div>
              </div>
              {activeClient.phone&&<div style={{color:T.muted,fontSize:13,marginBottom:4}}>👤 {activeClient.phone} (Primary Contact)</div>}
              {activeClient.email&&<div style={{color:T.muted,fontSize:13,marginBottom:4}}>✉️ {activeClient.email}</div>}
              {activeClient.phone&&<div style={{color:T.muted,fontSize:13}}>📱 +1 {activeClient.phone}</div>}
              {activeClient.notes&&<div style={{color:C.warning,fontSize:13,marginTop:8,background:C.warning+"11",borderRadius:8,padding:"8px 12px"}}>📝 {activeClient.notes}</div>}
            </div>

            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{color:T.text,fontWeight:700,fontSize:14}}>Active Works</div>
              <button onClick={()=>setModal("add-work")} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"8px 16px",fontWeight:700,fontSize:12}}>+ New Work</button>
            </div>

            {clientWorks(activeClient.id).length===0&&<Empty icon="🎯" msg="No works yet"/>}
            {clientWorks(activeClient.id).map(w=>(
              <div key={w.id} onClick={()=>openWork(w)} style={{background:T.surface,borderRadius:12,padding:bp.isMobile?"12px":"14px 18px",marginBottom:10,cursor:"pointer",border:`1px solid ${T.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,gap:8}}>
                  <div style={{minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                      <div style={{background:STATUS_COLOR[w.status]+"33",color:STATUS_COLOR[w.status],borderRadius:20,padding:"2px 10px",fontSize:10,fontWeight:700,textTransform:"uppercase"}}>{w.status}</div>
                      <div style={{display:"flex",gap:2}}>{Array.from({length:PRIORITY_STARS[w.priority]||1}).map((_,i)=><span key={i} style={{color:PRIORITY_COLOR[w.priority],fontSize:10}}>★</span>)}</div>
                    </div>
                    <div style={{color:T.text,fontWeight:700,fontSize:bp.isMobile?13:15,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.title}</div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:6}}>
                      {w.category&&<span style={{background:T.card,color:T.muted,borderRadius:6,padding:"2px 8px",fontSize:11}}>{w.category}</span>}
                      {w.tags?.slice(0,3).map(t=><span key={t} style={{background:C.accent+"22",color:C.accent,borderRadius:6,padding:"2px 8px",fontSize:11}}>{t}</span>)}
                    </div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{color:T.text,fontWeight:800,fontSize:14}}>₹{(w.budget||0).toLocaleString()}</div>
                    <div style={{color:T.muted,fontSize:10}}>{w.budget?"Fixed Rate":""}</div>
                  </div>
                </div>
                {w.deadline&&<div style={{color:isOverdue(w.deadline,w.status)?C.danger:T.muted,fontSize:11}}>📅 Due {fmtDate(w.deadline)}{isOverdue(w.deadline,w.status)?" 🚨":""}</div>}
                {w.revisions.length>0&&<div style={{height:2,background:T.card,borderRadius:2,marginTop:10}}><div style={{height:"100%",width:`${Math.min(100,w.revisions.length*20)}%`,background:C.accent,borderRadius:2}}/></div>}
              </div>
            ))}
          </div>
        )}

        {/* WORK DETAIL */}
        {view==="work-detail"&&activeWork&&(
          <WorkDetail work={activeWork} T={T} bp={bp} clients={clients}
            onAdvance={()=>advanceStatus(activeWork)}
            onRevision={note=>addRevision(activeWork,note)}
            onProgress={line=>addProgress(activeWork,line)}
            onDelivery={()=>toggleDelivered(activeWork)}
            onPayment={state=>setPayment(activeWork,state)}
            onNoteChange={notes=>saveNotes(activeWork,notes)}
          />
        )}
      </main>

      {/* MOBILE BOTTOM NAV */}
      {bp.isMobile&&(
        <nav style={{position:"fixed",bottom:0,left:0,right:0,background:T.surface,borderTop:`1px solid ${T.border}`,display:"flex",padding:"8px 0 12px",zIndex:100}}>
          {[["dashboard","📊","Dashboard"],["clients","👥","Clients"]].map(([k,icon,label])=>(
            <button key={k} onClick={()=>navTo(k)} style={{flex:1,background:"none",border:"none",color:(view===k||view==="client-detail"&&k==="clients"||view==="work-detail"&&k==="clients")?C.accent:T.muted,display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"4px 0"}}>
              <span style={{fontSize:20}}>{icon}</span>
              <span style={{fontSize:10,fontWeight:700}}>{label}</span>
            </button>
          ))}
        </nav>
      )}

      {/* MODALS */}
      {modal==="add-client"&&<AddClientModal T={T} bp={bp} onSave={async d=>{await addClient(d);setModal(null);}} onClose={()=>setModal(null)}/>}
      {modal==="add-work"&&activeClient&&<AddWorkModal T={T} bp={bp} clientId={activeClient.id} onSave={async d=>{await addWork(d);setModal(null);}} onClose={()=>setModal(null)}/>}
      {modal==="report"&&activeClient&&<ReportModal T={T} bp={bp} client={activeClient} works={clientWorks(activeClient.id)} clients={clients} onClose={()=>setModal(null)}/>}
    </div>
  );
}

// ── Work Detail ───────────────────────────────────────────────────────────────
function WorkDetail({work,T,bp,clients,onAdvance,onRevision,onProgress,onDelivery,onPayment,onNoteChange}){
  const [revNote,setRevNote]=useState("");
  const [progLine,setProgLine]=useState("");
  const [noteText,setNoteText]=useState(work.notes||"");
  const noteTimer=useRef(null);
  const idx=STATUS_FLOW.indexOf(work.status);
  const cl=clients.find(c=>c.id===work.client_id);
  const panel={background:T.surface,borderRadius:14,padding:bp.isMobile?"14px":"18px",border:`1px solid ${T.border}`,marginBottom:14};
  const inp={background:T.card,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,padding:"10px 13px",fontSize:13,outline:"none",width:"100%",boxSizing:"border-box",fontFamily:"inherit"};

  return (
    <div>
      {/* Header */}
      <div style={{...panel,marginBottom:14}}>
        <div style={{color:T.muted,fontSize:12,fontWeight:700,marginBottom:4}}>{cl?.name?.toUpperCase()||"CLIENT"}</div>
        <div style={{color:T.text,fontWeight:800,fontSize:bp.isMobile?18:22,marginBottom:4}}>{work.title}</div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:10}}>
          {work.deadline&&<div style={{color:isOverdue(work.deadline,work.status)?C.danger:T.muted,fontSize:12}}>📅 Due {fmtDate(work.deadline)}{isOverdue(work.deadline,work.status)?" 🚨":""}</div>}
          {work.category&&<span style={{background:T.card,color:T.muted,borderRadius:6,padding:"2px 8px",fontSize:11}}>{work.category}</span>}
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {work.tags?.map(t=><span key={t} style={{background:C.accent+"22",color:C.accent,border:`1px solid ${C.accent}33`,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:600}}>{t}</span>)}
        </div>
      </div>

      {/* Current Status */}
      <div style={panel}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={{color:T.muted,fontSize:11,fontWeight:700,letterSpacing:"0.06em"}}>CURRENT STATUS</div>
          <div style={{color:STATUS_COLOR[work.status],fontWeight:800,fontSize:13}}>{work.status}</div>
        </div>
        {/* Progress bar */}
        <div style={{position:"relative",marginBottom:16}}>
          <div style={{height:6,background:T.card,borderRadius:4,overflow:"hidden"}}>
            <div style={{height:"100%",background:STATUS_COLOR[work.status],width:`${(idx/(STATUS_FLOW.length-1))*100}%`,borderRadius:4,transition:"width 0.4s"}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:8}}>
            {STATUS_FLOW.map((st,i)=>(
              <div key={st} style={{textAlign:"center",flex:1}}>
                <div style={{width:10,height:10,borderRadius:"50%",background:i<=idx?STATUS_COLOR[work.status]:T.border,margin:"0 auto 4px",border:i===idx?`2px solid ${STATUS_COLOR[work.status]}`:""}}/>
                <div style={{color:i<=idx?T.text:T.muted,fontSize:9,fontWeight:i===idx?700:400,display:bp.isMobile?"none":"block"}}>{st}</div>
              </div>
            ))}
          </div>
          <div style={{color:T.muted,fontSize:11,textAlign:"center",marginTop:4}}>Step {idx+1} of {STATUS_FLOW.length}</div>
        </div>
        {work.status!=="Completed"
          ?<button onClick={onAdvance} style={{width:"100%",background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"13px",fontWeight:700,fontSize:14}}>Advance Status →</button>
          :<div style={{background:C.success+"22",color:C.success,borderRadius:10,padding:"13px",textAlign:"center",fontWeight:700}}>✅ Project Complete!</div>
        }
      </div>

      {/* Delivery + Actions */}
      <div style={panel}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div>
            <div style={{color:T.muted,fontSize:11,fontWeight:700,marginBottom:8,letterSpacing:"0.06em"}}>▶ DELIVERY</div>
            <button onClick={onDelivery} style={{width:"100%",background:work.delivered?C.success+"22":T.card,color:work.delivered?C.success:T.muted,border:`1.5px solid ${work.delivered?C.success:T.border}`,borderRadius:10,padding:"11px 8px",fontWeight:700,fontSize:bp.isMobile?11:12,textAlign:"center"}}>
              {work.delivered?"✓ Delivered":"Drafting"}
            </button>
          </div>
          <div>
            <div style={{color:T.muted,fontSize:11,fontWeight:700,marginBottom:8,letterSpacing:"0.06em"}}>⚡ ACTIONS</div>
            <button onClick={()=>onPayment(work.advance_received?"none":"advance")} style={{width:"100%",background:work.advance_received?C.accent:T.card,color:work.advance_received?"#fff":T.muted,border:`1.5px solid ${work.advance_received?C.accent:T.border}`,borderRadius:10,padding:"11px 8px",fontWeight:700,fontSize:bp.isMobile?11:12,textAlign:"center"}}>
              {work.advance_received?"✓ Adv. Received":"Adv. Received"}
            </button>
          </div>
        </div>
        {work.paid&&<div style={{marginTop:10,background:C.success+"22",color:C.success,borderRadius:10,padding:"10px",textAlign:"center",fontWeight:700,fontSize:13}}>💰 Full Payment Received</div>}
        {!work.paid&&work.advance_received&&(
          <button onClick={()=>onPayment("full")} style={{marginTop:10,width:"100%",background:C.success,color:"#fff",border:"none",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13}}>Mark Full Payment Received</button>
        )}
      </div>

      {/* Financial Overview */}
      <div style={panel}>
        <div style={{color:T.muted,fontSize:11,fontWeight:700,marginBottom:12,letterSpacing:"0.06em"}}>FINANCIAL OVERVIEW</div>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
          <div><div style={{color:T.muted,fontSize:11}}>Total Budget</div><div style={{color:T.text,fontWeight:800,fontSize:18}}>₹{(work.budget||0).toLocaleString()}</div></div>
          <div style={{textAlign:"right"}}><div style={{color:T.muted,fontSize:11}}>Remaining</div><div style={{color:work.paid?C.success:C.danger,fontWeight:800,fontSize:18}}>₹{((work.budget||0)-(work.advance||0)).toLocaleString()}</div></div>
        </div>
        <div style={{height:6,background:T.card,borderRadius:4,overflow:"hidden",marginBottom:10}}>
          <div style={{height:"100%",background:work.paid?C.success:C.accent,width:`${work.budget>0?Math.min(100,(work.advance/work.budget)*100):0}%`,borderRadius:4}}/>
        </div>
        <div style={{display:"flex",justifyContent:"space-between"}}>
          <div style={{color:T.muted,fontSize:12}}>{work.budget>0?Math.round((work.advance/work.budget)*100):0}% Advance Paid</div>
          <div style={{color:T.muted,fontSize:12}}>{work.paid?100:0}% Pending</div>
        </div>
      </div>

      {/* Revisions */}
      <div style={panel}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div style={{color:T.muted,fontSize:11,fontWeight:700,letterSpacing:"0.06em"}}>REVISIONS</div>
          <div style={{color:T.muted,fontSize:12}}>{work.revisions.length}/{work.revisions.length+3} Included</div>
        </div>
        {work.revisions.map((r,i)=>(
          <div key={r.id} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:`1px solid ${T.border}`,alignItems:"flex-start"}}>
            <div style={{width:22,height:22,borderRadius:"50%",background:C.accent+"22",color:C.accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,flexShrink:0}}>{i+1}</div>
            <div style={{flex:1,color:T.text,fontSize:13}}>{r.note}</div>
            <div style={{color:T.muted,fontSize:10,flexShrink:0}}>{fmt(r.created_at)}</div>
          </div>
        ))}
        {work.revisions.length===0&&<div style={{color:T.muted,fontSize:13,textAlign:"center",padding:"12px 0"}}>No revisions yet</div>}
        <div style={{display:"flex",gap:8,marginTop:12}}>
          <input style={{...inp,flex:1}} placeholder="Log new revision request…" value={revNote}
            onChange={e=>setRevNote(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter"&&revNote.trim()){onRevision(revNote.trim());setRevNote("");}}}/>
          <button onClick={()=>{if(revNote.trim()){onRevision(revNote.trim());setRevNote("");}}} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 16px",fontWeight:700,fontSize:13,flexShrink:0}}>+</button>
        </div>
      </div>

      {/* Workspace Notes */}
      <div style={panel}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{color:T.muted,fontSize:11,fontWeight:700,letterSpacing:"0.06em"}}>WORKSPACE NOTES</div>
          <div style={{color:T.muted,fontSize:10}}>🔄 Auto-saving</div>
        </div>
        <textarea style={{...inp,height:90,resize:"none",color:T.muted}} value={noteText}
          onChange={e=>{setNoteText(e.target.value);clearTimeout(noteTimer.current);noteTimer.current=setTimeout(()=>onNoteChange(e.target.value),800);}}
          placeholder="Jot down ideas, meeting notes, or daily progress…"/>
        {/* Progress */}
        <div style={{marginTop:12}}>
          <div style={{display:"flex",gap:8}}>
            <input style={{...inp,flex:1}} placeholder="Add daily progress…" value={progLine}
              onChange={e=>setProgLine(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"&&progLine.trim()){onProgress(progLine.trim());setProgLine("");}}}/>
            <button onClick={()=>{if(progLine.trim()){onProgress(progLine.trim());setProgLine("");}}} style={{background:T.card,color:T.muted,border:`1px solid ${T.border}`,borderRadius:10,padding:"10px 14px",fontWeight:700,fontSize:13}}>Save</button>
          </div>
          {work.progress.slice(-3).map((p,i)=>(
            <div key={p.id||i} style={{color:T.muted,fontSize:12,padding:"6px 0",borderBottom:`1px solid ${T.border}`}}>· {p.line}</div>
          ))}
        </div>
      </div>

      {/* Project History / Timeline */}
      <div style={panel}>
        <div style={{color:T.muted,fontSize:11,fontWeight:700,marginBottom:14,letterSpacing:"0.06em"}}>PROJECT HISTORY</div>
        <div style={{position:"relative",paddingLeft:24}}>
          <div style={{position:"absolute",left:8,top:6,bottom:6,width:2,background:T.border,borderRadius:2}}/>
          {[...work.timeline].reverse().map((t,i)=>(
            <div key={i} style={{position:"relative",paddingBottom:14}}>
              <div style={{position:"absolute",left:-24,top:2,width:10,height:10,borderRadius:"50%",background:i===0?C.accent:T.border,border:i===0?`2px solid ${C.accent}`:""}}/>
              <div style={{color:T.text,fontSize:13,fontWeight:600}}>{t.event}</div>
              <div style={{color:T.muted,fontSize:11,marginTop:2}}>{fmt(t.created_at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Report Modal ──────────────────────────────────────────────────────────────
function ReportModal({T,bp,client,works,onClose}){
  const total=works.reduce((a,w)=>a+(w.budget||0),0);
  const received=works.reduce((a,w)=>a+(w.paid?(w.budget||0):w.advance_received?(w.advance||0):0),0);
  const printReport=()=>{
    const w=window.open("","_blank");
    w.document.write(`<!DOCTYPE html><html><head><title>Report - ${client.name}</title><style>body{font-family:Inter,sans-serif;padding:32px;color:#111;max-width:800px;margin:0 auto}h1{color:#6C63FF;display:flex;align-items:center;gap:8px}h2{color:#333;margin-top:24px}table{width:100%;border-collapse:collapse;margin-top:12px}td,th{border:1px solid #e5e7eb;padding:10px 12px;text-align:left}th{background:#f9fafb;font-weight:700;color:#6b7280;font-size:12px;text-transform:uppercase}.box{display:inline-block;background:#f4f5fb;padding:14px 20px;border-radius:10px;margin:4px;min-width:140px}.badge{display:inline-block;padding:3px 10px;border-radius:20px;font-size:12px;font-weight:700}.paid{background:#22C55E22;color:#22C55E}.pending{background:#EF444422;color:#EF4444}.advance{background:#F59E0B22;color:#F59E0B}footer{margin-top:32px;color:#9ca3af;font-size:12px;border-top:1px solid #e5e7eb;padding-top:16px}</style></head><body>
    <h1>◈ Workflow — Client Report</h1>
    <h2>${client.name}${client.company?` <span style="font-weight:400;color:#6b7280">· ${client.company}</span>`:""}</h2>
    ${client.phone?`<p style="color:#6b7280;margin:4px 0">📱 ${client.phone}</p>`:""}
    ${client.email?`<p style="color:#6b7280;margin:4px 0">✉️ ${client.email}</p>`:""}
    <div style="margin:20px 0">
      <span class="box"><b style="color:#6C63FF">₹${total.toLocaleString()}</b><br><small style="color:#6b7280">Total Budget</small></span>
      <span class="box"><b style="color:#22C55E">₹${received.toLocaleString()}</b><br><small style="color:#6b7280">Received</small></span>
      <span class="box"><b style="color:#EF4444">₹${(total-received).toLocaleString()}</b><br><small style="color:#6b7280">Pending</small></span>
    </div>
    <table><tr><th>Work</th><th>Category</th><th>Status</th><th>Revisions</th><th>Delivered</th><th>Budget</th><th>Payment</th></tr>
    ${works.map(w=>`<tr><td><b>${w.title}</b></td><td>${w.category||"—"}</td><td>${w.status}</td><td>${w.revisions.length}</td><td>${w.delivered?"✓ Yes":"No"}</td><td>₹${(w.budget||0).toLocaleString()}</td><td><span class="${w.paid?"paid":w.advance_received?"advance":"pending"}">${w.paid?"Full Paid":w.advance_received?"Advance":"Pending"}</span></td></tr>`).join("")}
    </table>
    <footer>Generated by ◈ Workflow · ${new Date().toLocaleString("en-IN")} · workflow.loytro.com</footer>
    </body></html>`);
    w.document.close(); w.print();
  };
  const whatsapp=()=>{
    const msg=`*◈ Workflow Report*\n*Client:* ${client.name}\n*Total Works:* ${works.length}\n*Budget:* ₹${total.toLocaleString()}\n*Received:* ₹${received.toLocaleString()}\n*Pending:* ₹${(total-received).toLocaleString()}\n\n${works.map(w=>`• ${w.title} — ${w.status} ${w.paid?"✓ Paid":""}`).join("\n")}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`,"_blank");
  };
  return (
    <div style={{position:"fixed",inset:0,background:"#00000095",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:16,backdropFilter:"blur(6px)"}}>
      <div style={{background:T.surface,borderRadius:18,padding:bp.isMobile?"16px":"24px",width:"100%",maxWidth:500,maxHeight:"90vh",overflowY:"auto",border:`1px solid ${T.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <div style={{color:T.text,fontWeight:800,fontSize:16}}>📄 Client Report — {client.name}</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.muted,fontSize:22}}>✕</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:18}}>
          {[["Budget",total,C.accent],["Received",received,C.success],["Pending",total-received,C.danger]].map(([l,v,c])=>(
            <div key={l} style={{background:T.card,borderRadius:12,padding:"14px 10px",textAlign:"center",border:`1px solid ${T.border}`}}>
              <div style={{color:c,fontWeight:900,fontSize:17}}>₹{v.toLocaleString()}</div>
              <div style={{color:T.muted,fontSize:11,marginTop:3}}>{l}</div>
            </div>
          ))}
        </div>
        {works.map(w=>(
          <div key={w.id} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"10px 0",borderBottom:`1px solid ${T.border}`,gap:10}}>
            <div style={{minWidth:0}}>
              <div style={{color:T.text,fontWeight:600,fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.title}</div>
              <div style={{color:T.muted,fontSize:11,marginTop:2}}>{w.revisions.length} revisions · {w.delivered?"Delivered":"Not delivered"}</div>
            </div>
            <div style={{textAlign:"right",flexShrink:0}}>
              <SBadge status={w.status}/>
              <div style={{color:w.paid?C.success:w.advance_received?C.warning:C.danger,fontSize:11,marginTop:4,fontWeight:700}}>{w.paid?"Paid":w.advance_received?"Advance":"Pending"}</div>
            </div>
          </div>
        ))}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:18}}>
          <button onClick={whatsapp} style={{background:"#25D366",color:"#fff",border:"none",borderRadius:12,padding:"13px",fontWeight:700,fontSize:13}}>📤 WhatsApp Share</button>
          <button onClick={printReport} style={{background:C.accent,color:"#fff",border:"none",borderRadius:12,padding:"13px",fontWeight:700,fontSize:13}}>🖨️ Print / PDF</button>
        </div>
        <button onClick={onClose} style={{width:"100%",background:"none",border:`1px solid ${T.border}`,color:T.muted,borderRadius:12,padding:"11px",fontWeight:600,fontSize:13,marginTop:10}}>Close</button>
      </div>
    </div>
  );
}

// ── Add Client ────────────────────────────────────────────────────────────────
function AddClientModal({T,bp,onSave,onClose}){
  const [f,setF]=useState({name:"",phone:"",email:"",company:"",notes:""});
  const set=k=>e=>setF(p=>({...p,[k]:e.target.value}));
  const inp={background:T.card,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,padding:"12px 14px",fontSize:14,outline:"none",width:"100%",boxSizing:"border-box",fontFamily:"inherit"};
  return (
    <div style={{position:"fixed",inset:0,background:"#00000095",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:16,backdropFilter:"blur(6px)"}}>
      <div style={{background:T.surface,borderRadius:18,padding:bp.isMobile?"16px":"24px",width:"100%",maxWidth:420,border:`1px solid ${T.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <div style={{color:T.text,fontWeight:800,fontSize:16}}>👤 Add New Client</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.muted,fontSize:22}}>✕</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <input style={inp} placeholder="Client Name *" value={f.name} onChange={set("name")}/>
          <input style={inp} placeholder="Phone Number" value={f.phone} onChange={set("phone")} type="tel"/>
          <input style={inp} placeholder="Email Address" value={f.email} onChange={set("email")} type="email"/>
          <input style={inp} placeholder="Company / Organization" value={f.company} onChange={set("company")}/>
          <textarea style={{...inp,height:80,resize:"none"}} placeholder="Notes (optional)" value={f.notes} onChange={set("notes")}/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:18}}>
          <button onClick={()=>{if(f.name.trim())onSave(f);}} style={{background:C.accent,color:"#fff",border:"none",borderRadius:12,padding:"13px",fontWeight:700,fontSize:14}}>Save Client</button>
          <button onClick={onClose} style={{background:"none",border:`1px solid ${T.border}`,color:T.muted,borderRadius:12,padding:"13px",fontWeight:600,fontSize:14}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Add Work ──────────────────────────────────────────────────────────────────
function AddWorkModal({T,bp,clientId,onSave,onClose}){
  const [f,setF]=useState({client_id:clientId,title:"",category:"Reel",description:"",deadline:"",priority:"Low",tags:[],budget:"",advance:""});
  const set=k=>e=>setF(p=>({...p,[k]:e.target.value}));
  const toggleTag=t=>setF(p=>({...p,tags:p.tags.includes(t)?p.tags.filter(x=>x!==t):[...p.tags,t]}));
  const inp={background:T.card,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,padding:"12px 14px",fontSize:13,outline:"none",width:"100%",boxSizing:"border-box",fontFamily:"inherit"};
  return (
    <div style={{position:"fixed",inset:0,background:"#00000095",display:"flex",alignItems:bp.isMobile?"flex-end":"center",justifyContent:"center",zIndex:200,backdropFilter:"blur(6px)"}}>
      <div style={{background:T.surface,borderRadius:bp.isMobile?"18px 18px 0 0":"18px",padding:bp.isMobile?"18px 16px 24px":"24px",width:"100%",maxWidth:480,maxHeight:bp.isMobile?"92vh":"88vh",overflowY:"auto",border:`1px solid ${T.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <div style={{color:T.text,fontWeight:800,fontSize:16}}>🎯 New Work Item</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.muted,fontSize:22}}>✕</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <input style={inp} placeholder="Task or Project Title *" value={f.title} onChange={set("title")}/>
          <select style={{...inp,cursor:"pointer"}} value={f.category} onChange={set("category")}>
            {CATEGORIES.map(c=><option key={c}>{c}</option>)}
          </select>
          <textarea style={{...inp,height:70,resize:"none"}} placeholder="Description (optional)" value={f.description} onChange={set("description")}/>
          <input style={inp} type="date" value={f.deadline} onChange={set("deadline")}/>

          <div>
            <div style={{color:T.muted,fontSize:11,fontWeight:700,marginBottom:8,letterSpacing:"0.06em"}}>PRIORITY LEVEL</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
              {PRIORITIES.map((p,i)=>(
                <button key={p} onClick={()=>setF(prev=>({...prev,priority:p}))}
                  style={{borderRadius:10,padding:"10px 6px",fontWeight:700,fontSize:12,textAlign:"center",background:f.priority===p?PRIORITY_COLOR[p]+"22":T.card,color:f.priority===p?PRIORITY_COLOR[p]:T.muted,border:`1.5px solid ${f.priority===p?PRIORITY_COLOR[p]:T.border}`}}>
                  <div style={{marginBottom:3}}>{Array.from({length:i+1}).map((_,j)=><span key={j} style={{color:f.priority===p?PRIORITY_COLOR[p]:T.muted}}>★</span>)}</div>
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div style={{color:T.muted,fontSize:11,fontWeight:700,marginBottom:8,letterSpacing:"0.06em"}}>BUDGET ALLOCATION</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <input style={inp} type="number" placeholder="$ Budget" value={f.budget} onChange={set("budget")}/>
              <input style={inp} type="number" placeholder="$ Advance" value={f.advance} onChange={set("advance")}/>
            </div>
          </div>

          <div>
            <div style={{color:T.muted,fontSize:11,fontWeight:700,marginBottom:8,letterSpacing:"0.06em"}}>TAGS</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {TAGS.map(t=>(
                <button key={t} onClick={()=>toggleTag(t)}
                  style={{background:f.tags.includes(t)?C.accent:T.card,color:f.tags.includes(t)?"#fff":T.muted,border:`1px solid ${f.tags.includes(t)?C.accent:T.border}`,borderRadius:20,padding:"6px 12px",fontSize:11,fontWeight:600}}>
                  {t} {f.tags.includes(t)?"✕":"+"}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:18}}>
          <button onClick={()=>{if(f.title.trim())onSave(f);}} style={{background:C.accent,color:"#fff",border:"none",borderRadius:12,padding:"13px",fontWeight:700,fontSize:14}}>Create Work Item</button>
          <button onClick={onClose} style={{background:"none",border:`1px solid ${T.border}`,color:T.muted,borderRadius:12,padding:"13px",fontWeight:600,fontSize:14}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Shared ────────────────────────────────────────────────────────────────────
const Av=({name,size=44})=>{
  const colors=["#6C63FF","#22C55E","#F59E0B","#EF4444","#38BDF8","#A78BFA"];
  const color=colors[(name?.charCodeAt(0)||0)%colors.length];
  return <div style={{width:size,height:size,borderRadius:"50%",background:color+"33",color,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:size*0.38,flexShrink:0,border:`1.5px solid ${color}44`}}>{name?.[0]?.toUpperCase()||"?"}</div>;
};
const StatusDot=({status})=><div style={{width:10,height:10,borderRadius:"50%",background:STATUS_COLOR[status]||"#6B7280",flexShrink:0}}/>;
const SBadge=({status})=>{const c=STATUS_COLOR[status]||"#6B7280";return <span style={{background:c+"22",color:c,border:`1px solid ${c}44`,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>{status}</span>;};
const Empty=({icon,msg})=><div style={{textAlign:"center",padding:"28px 16px",color:"#6B7280"}}><div style={{fontSize:32,marginBottom:8}}>{icon}</div><div style={{fontSize:13}}>{msg}</div></div>;
const Toast=({msg,type})=><div style={{position:"fixed",bottom:80,left:"50%",transform:"translateX(-50%)",background:type==="success"?"#22C55E":"#EF4444",color:"#fff",padding:"12px 22px",borderRadius:12,fontWeight:700,fontSize:13,zIndex:9999,boxShadow:"0 4px 24px #0008",whiteSpace:"nowrap"}}>{type==="success"?"✓":"✕"} {msg}</div>;
const Loader=({T})=>(
  <div style={{minHeight:"100vh",background:T?.bg||"#0D0F1A",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16}}>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <div style={{width:32,height:32,background:C.accent,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",animation:"spin 2s linear infinite"}}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
    </div>
    <div style={{color:"#6B7280",fontSize:14,fontWeight:600}}>Loading Workflow…</div>
  </div>
);
